import { removeHost } from '../removeHost/removeHost';

/*
 * Internal-only backend hostnames: the per-tenant CMS/API gateway (*.empirie.cloud/.dev)
 * and raw Kubernetes service DNS names (*.svc.cluster.local) the CMS sometimes reports as
 * its own self/alternate links. Never fine to expose these to the browser.
 */
const INTERNAL_HOST_PATTERN = /(?:^|\.)(?:empirie\.(?:cloud|dev)|cluster\.local)$/i;

const isInternalUrl = (value: string): boolean => {
  try {
    const { protocol, hostname } = new URL(value);
    return /^https?:$/.test(protocol) && INTERNAL_HOST_PATTERN.test(hostname);
  } catch {
    return false;
  }
};

/*
 * Recursively walks a JSON-shaped value (as returned by the CMS JSON:API / GraphQL resolver)
 * and relativizes any absolute URL pointing at an internal *.empirie.cloud/*.empirie.dev host,
 * so the raw CMS backend hostname never reaches the client payload.
 */
export const sanitizeInternalUrls = <T>(value: T): T => {
  if (typeof value === 'string') {
    return (isInternalUrl(value) ? removeHost(value) : value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeInternalUrls(item)) as T;
  }

  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        // alternateLinks has its own dedicated hostname-rewrite (see cmsFetcher.ts /
        // getCmsPageContentGql.ts) and downstream consumers require it to stay a
        // fully-qualified absolute URL (e.g. `new URL(...)` with no base) - never relativize it here.
        key === 'alternateLinks' ? val : sanitizeInternalUrls(val),
      ]),
    ) as T;
  }

  return value;
};
